import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  useTheme,
  Switch,
  FormControlLabel,
  Divider,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  LocationOn as LocationIcon,
  Note as NoteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon2
} from '@mui/icons-material';
import { agendamentoService, clienteService, usuarioService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// =====================================================
// TIPOS E INTERFACES
// =====================================================

interface Agendamento {
  id: number;
  cliente_id: number;
  cliente_nome?: string;
  usuario_id: number;
  usuario_nome?: string;
  data: string;
  hora: string;
  duracao: number;
  tipo: string;
  status: string;
  cor?: string;
  observacoes?: string;
  created_at?: string;
  updated_at?: string;
}

interface AgendamentoFormData {
  cliente_id: string;
  usuario_id: string;
  data: string;
  hora: string;
  duracao: string;
  tipo: string;
  status: string;
  cor: string;
  observacoes: string;
}

interface Cliente {
  id: number;
  nome: string;
  email?: string;
  celular?: string;
}

interface Usuario {
  id: number;
  nome: string;
  email: string;
}

interface DeleteDialogState {
  open: boolean;
  id: number | null;
}

// =====================================================
// FUNÇÕES DE FORMATAÇÃO E VALIDAÇÃO
// =====================================================

const formatarData = (data: string): string => {
  if (!data) return '';
  const dataObj = new Date(data);
  return dataObj.toLocaleDateString('pt-BR');
};

const formatarDataHora = (data: string, hora: string): string => {
  if (!data || !hora) return '';
  const dataObj = new Date(`${data}T${hora}`);
  return dataObj.toLocaleString('pt-BR');
};

const validarData = (data: string): boolean => {
  if (!data) return false;
  const dataObj = new Date(data);
  return !isNaN(dataObj.getTime());
};

const validarHora = (hora: string): boolean => {
  if (!hora) return false;
  const horaRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return horaRegex.test(hora);
};

const validarCamposObrigatorios = (formData: AgendamentoFormData): Record<string, string> => {
  const erros: Record<string, string> = {};
  
  if (!formData.cliente_id) {
    erros.cliente_id = 'Cliente é obrigatório';
  }
  
  if (!formData.usuario_id) {
    erros.usuario_id = 'Usuário é obrigatório';
  }
  
  if (!formData.data) {
    erros.data = 'Data é obrigatória';
  } else if (!validarData(formData.data)) {
    erros.data = 'Data inválida';
  }
  
  if (!formData.hora) {
    erros.hora = 'Hora é obrigatória';
  } else if (!validarHora(formData.hora)) {
    erros.hora = 'Hora inválida';
  }
  
  if (!formData.duracao) {
    erros.duracao = 'Duração é obrigatória';
  } else if (parseInt(formData.duracao) <= 0) {
    erros.duracao = 'Duração deve ser maior que zero';
  }
  
  if (!formData.tipo?.trim()) {
    erros.tipo = 'Tipo é obrigatório';
  }
  
  return erros;
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const Agendamento: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  
  // =====================================================
  // ESTADOS PRINCIPAIS
  // =====================================================
  const [agendamentos, setAgendamentos] = useState<Agendamento[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Estados do formulário
  const [formData, setFormData] = useState<AgendamentoFormData>({
    cliente_id: '',
    usuario_id: '',
    data: new Date().toISOString().split('T')[0],
    hora: '09:00',
    duracao: '60',
    tipo: '',
    status: 'Agendado',
    cor: '#1976d2',
    observacoes: ''
  });
  
  // Estados de controle
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredAgendamentos, setFilteredAgendamentos] = useState<Agendamento[]>([]);
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({ open: false, id: null });

  // =====================================================
  // CARREGAR DADOS INICIAIS
  // =====================================================
  useEffect(() => {
    carregarDados();
  }, []);

  // Filtrar agendamentos
  useEffect(() => {
    if (searchTerm) {
      const filtered = agendamentos.filter(agendamento => {
        const cliente = clientes.find(c => c.id === agendamento.cliente_id);
        const usuario = usuarios.find(u => u.id === agendamento.usuario_id);
        const nomeCliente = cliente ? cliente.nome.toLowerCase() : '';
        const nomeUsuario = usuario ? usuario.nome.toLowerCase() : '';
        const tipo = agendamento.tipo.toLowerCase();
        const status = agendamento.status.toLowerCase();
        
        return nomeCliente.includes(searchTerm.toLowerCase()) ||
               nomeUsuario.includes(searchTerm.toLowerCase()) ||
               tipo.includes(searchTerm.toLowerCase()) ||
               status.includes(searchTerm.toLowerCase());
      });
      setFilteredAgendamentos(filtered);
    } else {
      setFilteredAgendamentos(agendamentos);
    }
  }, [searchTerm, agendamentos, clientes, usuarios]);

  // Carregar dados se for edição
  useEffect(() => {
    if (id && id !== 'novo') {
      carregarAgendamento(parseInt(id));
    }
  }, [id]);

  // =====================================================
  // FUNÇÕES PRINCIPAIS
  // =====================================================
  const carregarDados = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      
      const [agendamentosResponse, clientesResponse, usuariosResponse] = await Promise.all([
        agendamentoService.listar(),
        clienteService.listar(),
        usuarioService.listar()
      ]);
      
      // Corrigir estrutura de resposta - acessar .data.data primeiro
      setAgendamentos(agendamentosResponse.data?.data || agendamentosResponse.data || []);
      setClientes(clientesResponse.data?.data || clientesResponse.data || []);
      setUsuarios(usuariosResponse.data?.data || usuariosResponse.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const carregarAgendamento = async (agendamentoId: number): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      
      const agendamentoResponse = await agendamentoService.buscarPorId(agendamentoId);
      // Corrigir estrutura de resposta - acessar .data.data primeiro
      const agendamento = agendamentoResponse.data?.data || agendamentoResponse.data;
      setFormData({
        cliente_id: agendamento.cliente_id.toString(),
        usuario_id: agendamento.usuario_id.toString(),
        data: agendamento.data.split('T')[0],
        hora: agendamento.hora,
        duracao: agendamento.duracao.toString(),
        tipo: agendamento.tipo,
        status: agendamento.status,
        cor: agendamento.cor || '#1976d2',
        observacoes: agendamento.observacoes || ''
      });
      setIsEditing(true);
    } catch (error) {
      console.error('Erro ao carregar agendamento:', error);
      setError('Erro ao carregar agendamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof AgendamentoFormData, value: string): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erros quando o usuário começa a digitar
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    const erros = validarCamposObrigatorios(formData);
    if (Object.keys(erros).length > 0) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      const dadosParaEnviar = {
        ...formData,
        cliente_id: parseInt(formData.cliente_id),
        usuario_id: parseInt(formData.usuario_id),
        duracao: parseInt(formData.duracao),
        cor: formData.cor
      };
      
      if (isEditing && id) {
        await agendamentoService.atualizar(parseInt(id), dadosParaEnviar);
        setSuccess('Agendamento atualizado com sucesso!');
      } else {
        await agendamentoService.criar(dadosParaEnviar);
        setSuccess('Agendamento criado com sucesso!');
      }
      
      // Limpar formulário e voltar para lista
      setTimeout(() => {
        navigate('/app/agendamento');
      }, 1500);
      
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      setError('Erro ao salvar agendamento. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteDialog.id) return;
    
    try {
      setLoading(true);
      await agendamentoService.excluir(deleteDialog.id);
      setSuccess('Agendamento excluído com sucesso!');
      carregarDados();
      setDeleteDialog({ open: false, id: null });
    } catch (error) {
      console.error('Erro ao excluir agendamento:', error);
      setError('Erro ao excluir agendamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (agendamento: Agendamento): void => {
    navigate(`/app/agendamento/editar/${agendamento.id}`);
  };

  const handleNew = (): void => {
    navigate('/app/agendamento/novo');
  };

  const handleCancel = (): void => {
    navigate('/app/agendamento');
  };

  const limparFormulario = (): void => {
    setFormData({
      cliente_id: '',
      usuario_id: '',
      data: new Date().toISOString().split('T')[0],
      hora: '09:00',
      duracao: '60',
      tipo: '',
      status: 'Agendado',
      cor: '#1976d2',
      observacoes: ''
    });
    setError('');
    setIsEditing(false);
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'Agendado': return 'primary';
      case 'Confirmado': return 'success';
      case 'Cancelado': return 'error';
      case 'Realizado': return 'info';
      case 'Remarcado': return 'warning';
      default: return 'default';
    }
  };

  // =====================================================
  // RENDERIZAÇÃO DO FORMULÁRIO (EDIÇÃO/CRIAÇÃO)
  // =====================================================
  if (id) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={handleCancel} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="h1">
                {isEditing ? 'Editar Agendamento' : 'Novo Agendamento'}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Cliente</InputLabel>
                    <Select
                      value={formData.cliente_id}
                      onChange={(e) => handleInputChange('cliente_id', e.target.value)}
                      label="Cliente"
                      disabled={loading}
                    >
                      {clientes.map((cliente) => (
                        <MenuItem key={cliente.id} value={cliente.id}>
                          {cliente.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Usuário</InputLabel>
                    <Select
                      value={formData.usuario_id}
                      onChange={(e) => handleInputChange('usuario_id', e.target.value)}
                      label="Usuário"
                      disabled={loading}
                    >
                      {usuarios.map((usuario) => (
                        <MenuItem key={usuario.id} value={usuario.id}>
                          {usuario.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Data"
                    type="date"
                    value={formData.data}
                    onChange={(e) => handleInputChange('data', e.target.value)}
                    required
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Hora"
                    type="time"
                    value={formData.hora}
                    onChange={(e) => handleInputChange('hora', e.target.value)}
                    required
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ScheduleIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Duração (minutos)"
                    type="number"
                    value={formData.duracao}
                    onChange={(e) => handleInputChange('duracao', e.target.value)}
                    required
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ScheduleIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={formData.tipo}
                      onChange={(e) => handleInputChange('tipo', e.target.value)}
                      label="Tipo"
                      disabled={loading}
                    >
                      <MenuItem value="Consulta">Consulta</MenuItem>
                      <MenuItem value="Avaliação">Avaliação</MenuItem>
                      <MenuItem value="Retorno">Retorno</MenuItem>
                      <MenuItem value="Supervisão">Supervisão</MenuItem>
                      <MenuItem value="Outro">Outro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      label="Status"
                      disabled={loading}
                    >
                      <MenuItem value="Agendado">Agendado</MenuItem>
                      <MenuItem value="Confirmado">Confirmado</MenuItem>
                      <MenuItem value="Cancelado">Cancelado</MenuItem>
                      <MenuItem value="Realizado">Realizado</MenuItem>
                      <MenuItem value="Remarcado">Remarcado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Cor do compromisso */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Cor do compromisso</InputLabel>
                    <Select
                      value={formData.cor}
                      onChange={(e) => handleInputChange('cor', e.target.value as string)}
                      label="Cor do compromisso"
                      disabled={loading}
                    >
                      <MenuItem value="#1976d2">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 14, height: 14, bgcolor: '#1976d2', borderRadius: '3px' }} />
                          Azul (padrão)
                        </Box>
                      </MenuItem>
                      <MenuItem value="#2e7d32">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 14, height: 14, bgcolor: '#2e7d32', borderRadius: '3px' }} />
                          Verde
                        </Box>
                      </MenuItem>
                      <MenuItem value="#d32f2f">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 14, height: 14, bgcolor: '#d32f2f', borderRadius: '3px' }} />
                          Vermelho
                        </Box>
                      </MenuItem>
                      <MenuItem value="#6a1b9a">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 14, height: 14, bgcolor: '#6a1b9a', borderRadius: '3px' }} />
                          Roxo
                        </Box>
                      </MenuItem>
                      <MenuItem value="#f57c00">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 14, height: 14, bgcolor: '#f57c00', borderRadius: '3px' }} />
                          Laranja
                        </Box>
                      </MenuItem>
                      <MenuItem value="#fbc02d">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 14, height: 14, bgcolor: '#fbc02d', borderRadius: '3px' }} />
                          Amarelo
                        </Box>
                      </MenuItem>
                      <MenuItem value="#616161">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 14, height: 14, bgcolor: '#616161', borderRadius: '3px' }} />
                          Cinza
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observações"
                    value={formData.observacoes}
                    onChange={(e) => handleInputChange('observacoes', e.target.value)}
                    multiline
                    rows={3}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <NoteIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={saving}
                      startIcon={<CancelIcon />}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // =====================================================
  // RENDERIZAÇÃO DA LISTA DE AGENDAMENTOS
  // =====================================================
  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h1">
              Agendamentos
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNew}
            >
              Novo Agendamento
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por cliente, usuário, tipo ou status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Usuário</TableCell>
                    <TableCell>Data/Hora</TableCell>
                    <TableCell>Duração</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAgendamentos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Nenhum agendamento encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAgendamentos.map((agendamento) => {
                      const cliente = clientes.find(c => c.id === agendamento.cliente_id);
                      const usuario = usuarios.find(u => u.id === agendamento.usuario_id);
                      return (
                        <TableRow key={agendamento.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 2, bgcolor: theme.palette.primary.main }}>
                                <PersonIcon />
                              </Avatar>
                              {cliente ? cliente.nome : 'Cliente não encontrado'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {usuario ? usuario.nome : 'Usuário não encontrado'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                {formatarData(agendamento.data)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {agendamento.hora}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {agendamento.duracao} min
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={agendamento.tipo} 
                              color="secondary" 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={agendamento.status} 
                              color={getStatusColor(agendamento.status)} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(agendamento)}
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteDialog({ open: true, id: agendamento.id })}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este agendamento? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Agendamento;
